import type { ApolloClient } from '@apollo/client/core'
import { createGlobalGraphClient } from '@repo/services/global-client'
import type {
  VulcanClientRegistry as EmbeddedClientRegistry,
  VulcanEnvironment,
  VulcanRegion
} from '@repo/vulcan-tools'
import {
  type GatewayDefinition,
  gatewayKey,
  type VulcanCloud,
  type VulcanEnvironmentId,
  type VulcanGatewayKey,
  type VulcanRegionId
} from '../configuration/gateways'

type VulcanApolloClient = ApolloClient
type VulcanApolloClientMap = ReadonlyMap<VulcanEnvironmentId, ReadonlyMap<VulcanGatewayKey, VulcanApolloClient>>

export interface VulcanClientRegistry extends EmbeddedClientRegistry {
  clients: VulcanApolloClientMap
  defaultEnvironmentId: VulcanEnvironmentId
}

function createApolloClientMap(
  gateways: readonly GatewayDefinition[],
  getAccessToken: () => string | undefined
): Map<VulcanEnvironmentId, Map<VulcanGatewayKey, VulcanApolloClient>> {
  const clients = new Map<VulcanEnvironmentId, Map<VulcanGatewayKey, VulcanApolloClient>>()
  for (const gateway of gateways) {
    const environmentClients = clients.get(gateway.environmentId) ?? new Map<VulcanGatewayKey, VulcanApolloClient>()
    environmentClients.set(gateway.key, createGlobalGraphClient(`${gateway.baseUrl}/global/graphql`, getAccessToken))
    clients.set(gateway.environmentId, environmentClients)
  }
  return clients
}

export function createClientRegistry(
  gateways: readonly GatewayDefinition[],
  getAccessToken: () => string | undefined,
  defaultGatewayDefinition: GatewayDefinition = gateways[0]
): VulcanClientRegistry {
  if (!defaultGatewayDefinition) throw new Error('Vulcan requires at least one gateway.')
  const clientGateways = gateways.map((gateway) =>
    gateway.environmentId === defaultGatewayDefinition.environmentId && gateway.key === defaultGatewayDefinition.key
      ? { ...gateway, baseUrl: defaultGatewayDefinition.baseUrl, label: defaultGatewayDefinition.label }
      : gateway
  )
  const clients = createApolloClientMap(clientGateways, getAccessToken)
  const defaultClient = clients.get(defaultGatewayDefinition.environmentId)?.get(defaultGatewayDefinition.key)
  if (!defaultClient) throw new Error(`No Apollo client was created for ${defaultGatewayDefinition.label}.`)

  const environments = new Map<VulcanEnvironmentId, VulcanEnvironment>()
  const regions = new Map<VulcanRegionId, VulcanRegion>()
  const targets = clientGateways.map((gateway) => {
    const client = clients.get(gateway.environmentId)!.get(gateway.key)!
    if (!environments.has(gateway.environmentId) || gateway.isEnvironmentDefault) {
      environments.set(gateway.environmentId, {
        id: gateway.environmentId,
        label: gateway.environmentLabel,
        defaultClient: client
      })
    }
    regions.set(gateway.regionId, { id: gateway.regionId, label: gateway.regionLabel })
    return {
      id: `${gateway.environmentId}:${gateway.key}`,
      label: gateway.label,
      environmentId: gateway.environmentId,
      cloud: gateway.cloud,
      regionId: gateway.regionId,
      client
    }
  })
  environments.get(defaultGatewayDefinition.environmentId)!.defaultClient = defaultClient

  return {
    clients,
    targets,
    environments: Array.from(environments.values()),
    regions: Array.from(regions.values()),
    defaultClient,
    defaultEnvironmentId: defaultGatewayDefinition.environmentId,
    getClient(environmentId, cloud, regionId) {
      const environmentClients = clients.get(environmentId as VulcanEnvironmentId)
      const key = gatewayKey(cloud as VulcanCloud, regionId as VulcanRegionId)
      const client = environmentClients?.get(key)
      if (client) return client
      return environmentId === 'local' && cloud === 'PHOCAS' && environmentClients?.size === 1
        ? environmentClients.values().next().value
        : undefined
    }
  }
}

export function stopClientRegistry(registry: VulcanClientRegistry): void {
  for (const environmentClients of registry.clients.values()) {
    for (const client of environmentClients.values()) client.stop()
  }
}
